import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import { Box } from "@mui/material";
import tableMapping from "../../config/tableMapping.json";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "react-toastify";
import "../../styles/scss/DataTable.scss";
import { updateTableSortOrder, type SortOrderScope } from "./sortOrder.ts";

export interface Column {
  id: string;
  label: string;
  align?: "left" | "right" | "center";
  minWidth?: number;
  sortable?: boolean;
  format?: (value: any, row: any, index?: number) => React.ReactNode;
}

interface DataTableProps {
  columns: Column[];
  rows: any[];
  title?: string;
  onRowClick?: (row: any) => void;
  rowsPerPageOptions?: number[];
  totalCount: number;
  onPaginationChange?: (page: number, rowsPerPage: number) => void;
  page?: number;
  limit?: number;
  renderChildRow?: (row: any) => React.ReactNode;
  expandedRow?: number;
  selectable?: boolean;
  enableRowDrag?: boolean;
  tableName?: string;
  idField?: string;
  sortField?: string;
  scope?: SortOrderScope;
  onSortOrderChange?: () => void;
  onRowsReordered?: (rows: any[]) => void;
}

const getColumnClass = (align?: string) => {
  if (align === "center") return "center";
  if (align === "right") return "right";
  return "";
};

interface SortableTableRowProps {
  id: string;
  row: any;
  columns: Column[];
  index: number;
  onRowClick?: (row: any) => void;
}

function SortableTableRow({
  id,
  row,
  columns,
  index,
  onRowClick,
}: SortableTableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { zIndex: 1, position: "relative" } : {}),
  };

  return (
    <tr
      ref={setNodeRef}
      className={`data-grid-row draggable-row ${
        index % 2 === 1 ? "alternate" : ""
      } ${isDragging ? "dragging" : ""}`}
      style={style}
      {...attributes}
      {...listeners}
      onClick={() => onRowClick?.(row)}
    >
      <td className="drag-handle">
        <span
          className="drag-handle-icon material-symbols-outlined"
          aria-hidden="true"
        >
          drag_indicator
        </span>
      </td>
      {columns.map((column) => (
        <td key={column.id} className={getColumnClass(column.align)}>
          {column.format
            ? column.format(row[column.id], row, index)
            : row[column.id]}
        </td>
      ))}
    </tr>
  );
}

const rowKeyOf = (row: any, idField: string) => {
  const value = row?.[idField];
  return value === undefined || value === null ? "" : String(value);
};

const resolveTableName = (pathname: string): string | undefined => {
  const route = pathname.replace(/^\/admin/, "");
  const entry = (tableMapping as Record<string, string>)[route];
  return entry || (tableMapping as Record<string, string>)[`${route}/`];
};

export default function DataTable({
  columns,
  rows,
  title,
  onRowClick,
  rowsPerPageOptions = [10, 25, 50, 100],
  totalCount,
  onPaginationChange,
  page = 0,
  limit = 25,
  renderChildRow,
  expandedRow,
  selectable = false,
  enableRowDrag = false,
  tableName,
  idField = "id",
  sortField = "sort_order",
  scope,
  onSortOrderChange,
  onRowsReordered,
}: DataTableProps) {
  const location = useLocation();
  const resolvedTableName = tableName ?? resolveTableName(location.pathname);
  const [currentPage, setCurrentPage] = useState(page);
  const [rowsPerPage, setRowsPerPage] = useState(limit);
  const [localRows, setLocalRows] = useState(rows);

  useEffect(() => {
    setLocalRows(rows);
  }, [rows]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const rowIds = useMemo(
    () => localRows.map((row) => rowKeyOf(row, idField)),
    [localRows, idField],
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setCurrentPage(newPage);
    onPaginationChange?.(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(0);
    onPaginationChange?.(0, newRowsPerPage);
  };

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || String(active.id) === String(over.id)) return;

      const oldIndex = rowIds.indexOf(String(active.id));
      const newIndex = rowIds.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) return;

      const reorderedRows = arrayMove(localRows, oldIndex, newIndex);
      setLocalRows(reorderedRows);
      onRowsReordered?.(reorderedRows);

      if (!resolvedTableName) return;

      const rowId = reorderedRows[newIndex]
        ? reorderedRows[newIndex][idField]
        : String(active.id);
      const orderedIds = reorderedRows.map((row) => row[idField]);
      try {
        const response = await updateTableSortOrder({
          tableName: resolvedTableName,
          rowId,
          orderedIds,
          idField,
          sortField,
          offset: currentPage * rowsPerPage,
          scope,
        });
        if (response.status) {
          toast.success(response.message || "Order updated successfully");
          onSortOrderChange?.();
        } else {
          setLocalRows(rows);
        }
      } catch (error) {
        setLocalRows(rows);
        console.error("Failed to update sort order", error);
      }
    },
    [
      rowIds,
      localRows,
      resolvedTableName,
      idField,
      sortField,
      currentPage,
      rowsPerPage,
      scope,
      onSortOrderChange,
      onRowsReordered,
      rows,
    ],
  );

  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const currentPageNum = currentPage + 1;

  const renderPaginationNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPageNum <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPageNum >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPageNum - 1; i <= currentPageNum + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const colCount = columns.length + (enableRowDrag ? 1 : 0);

  const renderBody = () => {
    if (enableRowDrag) {
      return (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={rowIds}
            strategy={verticalListSortingStrategy}
          >
            {localRows.map((row, index) => (
              <React.Fragment key={rowKeyOf(row, idField) || index}>
                <SortableTableRow
                  id={rowKeyOf(row, idField)}
                  row={row}
                  columns={columns}
                  index={index}
                  onRowClick={onRowClick}
                />
                {expandedRow === index && renderChildRow && (
                  <tr className="expanded-row">
                    <td colSpan={colCount}>{renderChildRow(row)}</td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </SortableContext>
        </DndContext>
      );
    }

    return localRows.map((row, index) => (
      <React.Fragment key={index}>
        <tr
          className={`data-grid-row ${index % 2 === 1 ? "alternate" : ""}`}
          onClick={() => onRowClick?.(row)}
        >
          {columns.map((column) => (
            <td key={column.id} className={getColumnClass(column.align)}>
              {column.format
                ? column.format(row[column.id], row, index)
                : row[column.id]}
            </td>
          ))}
        </tr>
        {expandedRow === index && renderChildRow && (
          <tr className="expanded-row">
            <td colSpan={columns.length}>{renderChildRow(row)}</td>
          </tr>
        )}
      </React.Fragment>
    ));
  };

  return (
    <Box className="data-table-container">
      {title && (
        <Box className="data-table-header">
          <Box className="data-table-title">{title}</Box>
        </Box>
      )}

      <Box className="data-table-wrapper">
        <table className="data-table">
          <thead className="table-head">
            <tr>
              {enableRowDrag && (
                <th className="drag-handle-header">
                  <span className="drag-handle-icon material-symbols-outlined">
                    drag_indicator
                  </span>
                </th>
              )}
              {columns.map((column) => (
                <th
                  key={column.id}
                  className={`${getColumnClass(column.align)} ${column.sortable ? "sortable" : ""}`}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody
            className={`table-body ${enableRowDrag ? "drag-disabled" : ""}`}
          >
            {renderBody()}
          </tbody>
        </table>

        {localRows.length === 0 && (
          <Box className="table-empty">
            <span className="material-symbols-outlined">inbox</span>
            <p>No data available</p>
          </Box>
        )}
      </Box>

      {totalCount > 0 && (
        <Box className="table-pagination">
          <Box className="pagination-info">
            <span>{totalCount}</span> records
            <select value={rowsPerPage} onChange={handleChangeRowsPerPage}>
              {rowsPerPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option} / page
                </option>
              ))}
            </select>
          </Box>

          <Box className="pagination-controls">
            <button
              className="pagination-btn nav"
              onClick={() => handleChangePage(null, 0)}
              disabled={currentPage === 0}
            >
              <span className="material-symbols-outlined">first_page</span>
            </button>
            <button
              className="pagination-btn nav"
              onClick={() => handleChangePage(null, currentPage - 1)}
              disabled={currentPage === 0}
            >
              <span className="material-symbols-outlined">chevron_left</span>
            </button>

            {renderPaginationNumbers().map((pageNum, idx) =>
              pageNum === "..." ? (
                <span key={idx} className="pagination-ellipsis">
                  ...
                </span>
              ) : (
                <button
                  key={idx}
                  className={`pagination-btn ${currentPageNum === pageNum ? "active" : ""}`}
                  onClick={() =>
                    handleChangePage(null, (pageNum as number) - 1)
                  }
                >
                  {pageNum}
                </button>
              ),
            )}

            <button
              className="pagination-btn nav"
              onClick={() => handleChangePage(null, currentPage + 1)}
              disabled={currentPage >= totalPages - 1}
            >
              <span className="material-symbols-outlined">chevron_right</span>
            </button>
            <button
              className="pagination-btn nav"
              onClick={() => handleChangePage(null, totalPages - 1)}
              disabled={currentPage >= totalPages - 1}
            >
              <span className="material-symbols-outlined">last_page</span>
            </button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
